import React, { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  Checkbox,
  Container,
  Paper,
} from "@mui/material";
import { useAuthContext } from "../contexts/AuthContext";
import { AuthLayout } from "../components/Layouts";
import { createLoginStyles } from "./styles";
import { ROUTES } from "../constants/routes";
import { useAuth } from "../hooks/useAuth";
import { useTheme } from "@mui/material/styles";
import { RegistrationPayload } from "../types/api";
import { privacyNoticeUrl, termsAndConditionsUrl } from "../constants";

/**
 * Interface for the registration form data.
 * @interface FormData
 */
interface FormData extends RegistrationPayload {}

/**
 * Register Page Component
 *
 * Recreates the registration page using Material UI.
 * Handles user registration by calling the new /api/register endpoint.
 */
const Register: React.FC = () => {
  // Hooks
  const { registerUser } = useAuth();
  const { isAuthenticated } = useAuthContext();
  const navigate = useNavigate();

  // Styles
  const theme = useTheme();
  const loginStyles = createLoginStyles(theme);

  // State
  const [formData, setFormData] = useState<FormData>({
    name: "",
    last_name: "",
    email: "",
    password: "",
    password_confirmation: "",
    commercial_consent: false,
  });
  const [error, setError] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Handle form input changes
  const onFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setIsLoading(true);

    try {
      await registerUser(formData);
      setSuccessMessage("Registration successful! You can now log in.");
      navigate(ROUTES.LOGIN, {
        state: {
          message: "Registration successful! You can now log in.",
        },
      });
    } catch (err: any) {
      // Assuming a generic error message for now, but can be improved
      setError(
        err.message || "Registration failed. Please check your details."
      );
    } finally {
      setIsLoading(false);
    }
  };

  // If already authenticated, redirect to the dashboard
  if (isAuthenticated) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return (
    <AuthLayout>
      <title>Register with the Materiom Commons</title>
      <Container sx={loginStyles.container}>
        <Paper elevation={24} sx={loginStyles.paper}>
          {/* Page Header */}
          <Typography variant="h4" component="h2" sx={loginStyles.header}>
            Get Started
          </Typography>

          <Typography variant="body1" sx={loginStyles.subtitle}>
            Already have an account?{" "}
            <Link href={ROUTES.LOGIN} sx={loginStyles.registerLink}>
              login
            </Link>
          </Typography>

          {/* Alert Messages */}
          {error && (
            <Alert severity="error" sx={loginStyles.errorAlert}>
              {error}
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success" sx={loginStyles.errorAlert}>
              {successMessage}
            </Alert>
          )}

          {/* Registration Form */}
          <Box component="form" onSubmit={handleSubmit}>
            <Box sx={loginStyles.inputContainer}>
              <Typography
                variant="body2"
                component="label"
                sx={loginStyles.fieldLabel}
              >
                First Name
              </Typography>
              <TextField
                fullWidth
                type="text"
                name="name"
                value={formData.name}
                onChange={onFormChange}
                required
                autoFocus
                variant="outlined"
                sx={loginStyles.textField}
              />
            </Box>

            <Box sx={loginStyles.inputContainer}>
              <Typography
                variant="body2"
                component="label"
                sx={loginStyles.fieldLabel}
              >
                Last Name
              </Typography>
              <TextField
                fullWidth
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={onFormChange}
                required
                variant="outlined"
                sx={loginStyles.textField}
              />
            </Box>

            <Box sx={loginStyles.inputContainer}>
              <Typography
                variant="body2"
                component="label"
                sx={loginStyles.fieldLabel}
              >
                Email
              </Typography>
              <TextField
                fullWidth
                type="email"
                name="email"
                value={formData.email}
                onChange={onFormChange}
                required
                autoComplete="email"
                variant="outlined"
                sx={loginStyles.textField}
              />
            </Box>

            <Box sx={loginStyles.inputContainer}>
              <Typography
                variant="body2"
                component="label"
                sx={loginStyles.fieldLabel}
              >
                Password
              </Typography>
              <TextField
                fullWidth
                type="password"
                name="password"
                value={formData.password}
                onChange={onFormChange}
                required
                autoComplete="new-password"
                variant="outlined"
                sx={loginStyles.textField}
              />
            </Box>

            <Box sx={loginStyles.inputContainer}>
              <Typography
                variant="body2"
                component="label"
                sx={loginStyles.fieldLabel}
              >
                Confirm Password
              </Typography>
              <TextField
                fullWidth
                type="password"
                name="password_confirmation"
                value={formData.password_confirmation}
                onChange={onFormChange}
                required
                autoComplete="new-password"
                variant="outlined"
                sx={loginStyles.textField}
              />
            </Box>

            {/* Commercial Consent */}
            <Box sx={loginStyles.checkboxContainer}>
              <Checkbox
                name="commercial_consent"
                checked={formData.commercial_consent}
                onChange={onFormChange}
                sx={loginStyles.checkbox}
              />
              <Typography
                variant="body2"
                component="div"
                sx={loginStyles.rememberMeLabel}
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    commercial_consent: !prev.commercial_consent,
                  }))
                }
              >
                Sign up to our newsletter, and authorize Materiom to contact you
                about commercial ventures or to announce events you might be
                interested in.
              </Typography>
            </Box>

            {/* Submit Button */}
            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={isLoading}
              sx={loginStyles.loginButton}
              disableRipple
            >
              {isLoading ? "Signing up..." : "Sign Up"}
            </Button>

            {/* Terms & Privacy Links */}
            <Typography
              variant="body2"
              sx={{
                textAlign: "center",
                color: "text.secondary",
                mt: 2,
                fontSize: "0.8rem",
              }}
            >
              By signing up, you agree with Materiom's{" "}
              <Link
                href={privacyNoticeUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={loginStyles.registerLink}
              >
                Privacy Policy
              </Link>{" "}
              and{" "}
              <Link
                href={termsAndConditionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={loginStyles.registerLink}
              >
                Terms & Conditions
              </Link>
              .
            </Typography>
          </Box>
        </Paper>
      </Container>
    </AuthLayout>
  );
};

export default Register;
